import { Col, Stack, Container } from "react-bootstrap";
import { useMemo, useState } from "react";
import LoadingIndicator from "../LoadingIndicator";
import ErrorResponse from "../ErrorResponse";
import ExportData from "../ExportData";
import useSortableData from "../useSortableData";
import moment from "moment/min/moment-with-locales";
import SongSearchInput from "../SearchInput";
import EmptyRow from "../EmptyRow";
import { useResolvedRadioNames } from "../HelperHooks";
import SongsHeader from "./SongsHeader";
import SongRow from "./SongRow";
import SongCatalogueModal from "../songCatalogue/SongCatalogueModal";

export default function LatestNewMusicTable({ data, loading, error }) {
    const [catalogueSong, setCatalogueSong] = useState(null);
    const [filterData, setFilterData] = useState({ searchString: "" });

    const resolvedRadios = useResolvedRadioNames();

    const competitorName = useMemo(() => resolvedRadios?.find(x => x.radio_id === data?.market_id)?.radio_name, [data?.market_id, resolvedRadios]);

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.songs;
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.artist.toLowerCase();
                    const title = x.title.toLowerCase();
                    const first_play = moment(x.first_play).format("L")?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || regex.exact(first_play);
                            }

                            return artist.includes(term) || title.includes(term) || first_play.includes(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo, { key: "spins", direction: "descending" });

    const csvHeaders = ["Artist", "Title", "First played"];
    const csvData = sortedItems?.map(x => [x.artist, x.title, moment(x.first_play).format("L HH:mm")]);
    const csvFilename = useMemo(() => `Latest New Songs On ${competitorName}`, [competitorName]);

    return (
        <>
            {loading ? (
                <LoadingIndicator width={75} />
            ) : error ? (
                <ErrorResponse errorCode={error} />
            ) : (
                data && (
                    <>
                        <Stack direction="horizontal" gap={2} className=" mb-2 flex-wrap-reverse">
                            <Col xs xl={5}>
                                <SongSearchInput filterData={filterData} setFilterData={setFilterData} />
                            </Col>
                            <Col xs={"auto"} className="ms-auto">
                                <Stack direction="horizontal" gap={1} className="ms-xl-auto w-100 w-xl-auto order-first order-xl-last">
                                    <span className="me-1 d-none d-md-inline-block">Download:</span>
                                    <ExportData data={csvData} headers={csvHeaders} filename={csvFilename} />
                                </Stack>
                            </Col>
                        </Stack>
                        <SongsHeader sortConfig={sortConfig} requestSort={requestSort} showFirstPlay firstPlayColXl={3} firstPlayColXs={4} />
                        <Container fluid>
                            {sortedItems?.length ? (
                                sortedItems.map(x => <SongRow key={x.item_id} item={x} setCatalogueSong={setCatalogueSong} showFirstPlay firstPlayColXl={3} />)
                            ) : (
                                <EmptyRow text="No Songs" />
                            )}
                        </Container>
                    </>
                )
            )}
            <SongCatalogueModal show={!!catalogueSong} onHide={() => setCatalogueSong(null)} song={catalogueSong} />
        </>
    );
}
